import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { StripeSubscription, UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';
import { pluralize, wholeCreditsLeft } from '@flintmint/core-shared/utils';
import { Button } from '@flintmint/web-shared/components/button';

import { FullScreenOverlay, FullScreenOverlayPresence, OverlayNote, OverlayPanel } from '@/components/full-screen-overlay';
import { appRoutes } from '@/constants/routes';
import { urlOverlays } from '@/constants/url-overlays';
import { creditedJouleBalance } from '@/features/llm-chat/energy/energy-view';
import { useAppSettings } from '@/hooks/use-app-settings';
import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { useUserAccount } from '@/hooks/use-user-account';

import {
  addCalendarMonths,
  formatBillingDate,
  formatMonthlyEnergyRow,
  formatPrice,
  formatPriceWithCents,
} from '../billing-format';
import { canStartPlanEarly, heldChargeUrl } from '../billing-state';
import { useEndTrial } from '../use-cancel-subscription';

import styles from './start-plan.module.scss';

/**
 * Mounted once over every screen; the `startPlan` and `payHeldCharge` Energy actions, Settings' Start subscription now
 * and Complete payment, and the held early start banner open it.
 */
export const StartPlan = () => {
  const { isOpen, close } = useUrlOverlay(urlOverlays.startPlan, urlOverlays);

  return (
    <FullScreenOverlayPresence open={isOpen}>
      <StartPlanGate onClose={close} />
    </FullScreenOverlayPresence>
  );
};

/**
 * A held early start shows the held step whenever the overlay opens, including right after a declined confirm.
 * `confirmed` marks an early start made in this overlay and `heldShown` a held step shown in it. Without either, any
 * account the start is not offered to closes the overlay unseen, so Back, a reload or a shared link shows nothing,
 * and a success appears only after its own confirm or held step, once. A failed request or an expired hold leaves the
 * account offering the start, which clears both flags. The Joule balance is read at confirm because the sync credits
 * the unused Trial Joules in the same write that makes the subscription active, and a Trial cannot buy Recharge, so
 * its increase is exactly what the start carried over.
 */
const StartPlanGate = ({ onClose }: { onClose: () => void }) => {
  const { account, loading } = useUserAccount();
  const { endTrial, isPending } = useEndTrial();
  const [confirmed, setConfirmed] = useState(false);
  const [heldShown, setHeldShown] = useState(false);
  const [balanceAtConfirm, setBalanceAtConfirm] = useState(0);
  const subscription = account?.stripeSubscription;
  const offered = canStartPlanEarly(account);
  const heldUrl = isPending ? undefined : heldChargeUrl(account);
  const started = (confirmed || heldShown) && !isPending && subscription?.status === 'active';

  useEffect(() => {
    if (loading || isPending) return;
    if (heldUrl) setHeldShown(true);
    else if (offered) {
      setConfirmed(false);
      setHeldShown(false);
    } else if (!started) onClose();
  }, [loading, isPending, offered, started, heldUrl, onClose]);

  const confirm = () => {
    setConfirmed(true);
    setBalanceAtConfirm(jouleBalanceTotal(account));
    void endTrial();
  };

  return (
    subscription &&
    (offered || confirmed || !!heldUrl || started) && (
      <StartPlanDialog
        subscription={subscription}
        trialJoulesLeft={wholeCreditsLeft(account?.monthlyQuota)}
        carriedOverJoules={heldShown ? undefined : Math.floor(jouleBalanceTotal(account) - balanceAtConfirm)}
        started={started}
        heldUrl={heldUrl}
        pending={isPending}
        onConfirm={confirm}
        onClose={onClose}
      />
    )
  );
};

const jouleBalanceTotal = (account: UserAccountDoc | undefined) => creditedJouleBalance(account)?.total ?? 0;

type StartPlanDialogProps = {
  subscription: StripeSubscription;
  /** Whole Trial Joules left now; the confirm step's estimate of what the start carries over. */
  trialJoulesLeft: number;
  /**
   * Whole Joules the Joule balance gained since confirm; what the start carried over once it succeeds. `undefined`
   * once a held step was shown, when the confirm-time balance no longer measures the start.
   */
  carriedOverJoules: number | undefined;
  started: boolean;
  heldUrl: string | undefined;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const StartPlanDialog = ({
  subscription,
  trialJoulesLeft,
  carriedOverJoules,
  started,
  heldUrl,
  pending,
  onConfirm,
  onClose,
}: StartPlanDialogProps) => {
  const creditsPerTypicalNote = useAppSettings()?.creditsPerTypicalNote;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { planName, monthlyGrant, interval, amountCents } = subscription;
  // Leaving would hide a start that is still being applied.
  const closeUnlessPending = () => {
    if (!pending) onClose();
  };
  // A push keeps the page the user came from in history.
  const openSubscriptionBilling = () => {
    onClose();
    if (pathname !== appRoutes.settingsBilling) void navigate(appRoutes.settingsBilling);
  };

  const step = heldUrl ? 'held' : started ? 'success' : 'confirm';
  const carriesOver = step === 'confirm' && trialJoulesLeft >= 1;

  return (
    <FullScreenOverlay title={stepTitles[step]} subtitle={stepSubtitles[step]} view={step} onEscape={closeUnlessPending}>
      {heldUrl ? (
        <HeldStep heldUrl={heldUrl} onBack={openSubscriptionBilling} />
      ) : (
        <>
          <OverlayPanel title={planName}>
            <ChargeRows
              firstLabel={started ? 'Paid today' : 'Due today'}
              amountCents={amountCents}
              billingBeginsAt={carriesOver ? new Date().toISOString() : undefined}
              nextPaymentAt={started ? subscription.currentPeriodEnd : nextPaymentFromToday(interval)}
            />
            {monthlyGrant !== undefined && <div>{formatMonthlyEnergyRow(monthlyGrant, creditsPerTypicalNote)}</div>}
            {carriesOver && <div>Trial Energy carried over after payment: {pluralize(trialJoulesLeft, 'Joule')}</div>}
            {started &&
              (carriedOverJoules === undefined ? (
                <div>Unused Trial Energy is added to the Joule balance once, when payment succeeds.</div>
              ) : (
                carriedOverJoules >= 1 && (
                  <div>Joule balance: {pluralize(carriedOverJoules, 'unused Trial Joule')} carried over</div>
                )
              ))}
          </OverlayPanel>
          <OverlayNote>
            {started
              ? 'Your Monthly Energy starts at 100%. No charge is due on the original Trial end date.'
              : carriesOver
              ? 'After payment, your unused Trial Energy moves to your Joule balance. The amount is based on what remains when payment succeeds.'
              : 'Confirming ends Trial and starts billing today. You will not be charged again on the original Trial end date.'}
          </OverlayNote>
          <div className={styles.actions}>
            {started ? (
              <Button size="big" fullWidth cornerRadius="smooth" appearance="clear" onClick={openSubscriptionBilling}>
                View subscription
              </Button>
            ) : (
              <>
                <Button size="big" fullWidth cornerRadius="smooth" loading={pending} onClick={onConfirm}>
                  {`Confirm and pay ${formatPrice(amountCents)}`}
                </Button>
                <Button
                  size="big"
                  fullWidth
                  cornerRadius="smooth"
                  appearance="clear"
                  disabled={pending}
                  onClick={closeUnlessPending}
                >
                  Decide later
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </FullScreenOverlay>
  );
};

const stepTitles = {
  confirm: 'Start your plan today',
  success: 'Your paid plan is ready',
  held: 'Complete your subscription payment',
};

const stepSubtitles = {
  confirm: 'Get your plan’s Monthly Energy today.',
  success: undefined,
  held: 'Complete the existing early-start payment securely in Stripe.',
};

/** Ending the Trial moves the billing anchor to today, so the next payment is one interval from now. */
const nextPaymentFromToday = (interval: StripeSubscription['interval']) =>
  addCalendarMonths(new Date(), interval === 'year' ? 12 : 1).toISOString();

type ChargeRowsProps = {
  firstLabel: 'Due today' | 'Paid today';
  /** The subscription's synced price, before tax. */
  amountCents: number;
  /** ISO date; the row is left out without it. */
  billingBeginsAt: string | undefined;
  /** ISO date. */
  nextPaymentAt: string;
};

const ChargeRows = ({ firstLabel, amountCents, billingBeginsAt, nextPaymentAt }: ChargeRowsProps) => {
  const price = formatPriceWithCents(amountCents);

  return (
    <>
      <div>{`${firstLabel}: ${price} USD`}</div>
      {billingBeginsAt && <div>Billing begins: {formatBillingDate(billingBeginsAt)}</div>}
      <div>{`Next payment: ${price} on ${formatBillingDate(nextPaymentAt)}`}</div>
    </>
  );
};

/** Stripe's hosted invoice page has no return URL, so it opens in a new tab and the overlay waits for the payment. */
const HeldStep = ({ heldUrl, onBack }: { heldUrl: string; onBack: () => void }) => (
  <>
    <OverlayPanel title="Early-start payment">
      Pay the existing invoice before it expires. Your Trial continues unchanged until payment succeeds.
    </OverlayPanel>
    <OverlayNote>You’ll return to DocuMint after making your changes.</OverlayNote>
    <div className={styles.actions}>
      <Button size="big" fullWidth cornerRadius="smooth" onClick={() => window.open(heldUrl, '_blank', 'noopener,noreferrer')}>
        Open payment page
      </Button>
      <Button size="big" fullWidth cornerRadius="smooth" appearance="clear" onClick={onBack}>
        Back to Subscription & Billing
      </Button>
    </div>
  </>
);
